import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Eye, EyeOff, Mail, Lock, AlertCircle, Shield, Users, Zap, ArrowRight, User } from "lucide-react";
import authService from "@/services/auth.service";
import useAuthStore from "@/stores/authStore";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Alert, AlertDescription } from "./ui/alert";
import { useTranslation } from 'react-i18next';

const Register = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [acceptMarketing, setAcceptMarketing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const { setUser, setIsAuthenticated } = useAuthStore();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (formData.password !== formData.confirmPassword) {
      setError("Die Passwörter stimmen nicht überein");
      return;
    }

    if (formData.password.length < 8) {
      setError("Das Passwort muss mindestens 8 Zeichen lang sein");
      return;
    }

    // Check password complexity
    const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]/;
    if (!passwordRegex.test(formData.password)) {
      setError("Das Passwort muss mindestens einen Großbuchstaben, einen Kleinbuchstaben, eine Zahl und ein Sonderzeichen enthalten");
      return;
    }

    if (!acceptTerms) {
      setError("Bitte akzeptieren Sie die Allgemeinen Geschäftsbedingungen");
      return;
    }

    setIsLoading(true);

    try {
      const response = await authService.register({
        email: formData.email,
        password: formData.password,
        firstName: formData.firstName,
        lastName: formData.lastName,
        role: 'customer'
      });
      
      if (response.success) {
        setUser(response.data.user);
        setIsAuthenticated(true);
        
        // Store marketing preference
        if (acceptMarketing) {
          localStorage.setItem('marketingConsent', 'true');
        }
        
        navigate("/tickets");
      }
    } catch (error: any) {
      if (error.response?.status === 429) {
        setError("Zu viele Registrierungsversuche. Bitte versuchen Sie es in 15 Minuten erneut.");
      } else if (error.response?.data?.errors) {
        const validationErrors = error.response.data.errors;
        const errorMessages = validationErrors.map((err: any) => err.message).join(", ");
        setError(errorMessages);
      } else if (error.response?.data?.message) {
        setError(error.response.data.message);
      } else {
        setError("Registrierung fehlgeschlagen. Bitte versuchen Sie es erneut.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white flex">
      {/* Left Side - Dark Sidebar */}
      <div className="hidden lg:flex lg:w-[45%] bg-[#0f172a] relative overflow-hidden flex-col justify-between p-12">
        {/* Background Elements */}
        <div className="absolute top-0 right-0 w-96 h-96 bg-[#0F99FD]/10 rounded-full blur-3xl -translate-y-1/2 translate-x-1/2"></div>
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-900/10 rounded-full blur-3xl translate-y-1/2 -translate-x-1/2"></div>
        
        {/* Logo */}
        <div className="relative z-10">
          <img 
            src="/brand/vyndesk-logo-light.png" 
            alt="VynDesk Logo" 
            className="h-10 w-auto"
          />
        </div>

        {/* Hero Content */}
        <div className="relative z-10 max-w-lg">
          <h1 className="text-4xl font-bold text-white mb-6 leading-tight">
            {t('auth:register.joinTitle', 'Werden Sie Teil von')} <span className="text-[#0F99FD]">Vyndesk</span>
          </h1>
          <p className="text-lg text-slate-400 mb-8 leading-relaxed">
            Erstellen Sie Ihr Konto und revolutionieren Sie Ihren Kundensupport. 
            Einfach, leistungsstark und skalierbar.
          </p>
          
          <div className="space-y-4">
            <div className="flex items-center gap-3 text-slate-300">
              <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <Users className="h-5 w-5 text-[#0F99FD]" />
              </div>
              <div>
                <p className="font-medium text-white">{t('auth:register.b1', 'Schneller Start')}</p>
                <p className="text-sm text-slate-500">{t('auth:register.b1d', 'In wenigen Minuten eingerichtet')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <Zap className="h-5 w-5 text-[#0F99FD]" />
              </div>
              <div>
                <p className="font-medium text-white">{t('auth:register.b2', 'Sofortige Effizienz')}</p>
                <p className="text-sm text-slate-500">{t('auth:register.b2d', 'Automatisierte Workflows ab Tag 1')}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 text-slate-300">
              <div className="h-10 w-10 rounded-lg bg-white/5 border border-white/10 flex items-center justify-center">
                <Shield className="h-5 w-5 text-[#0F99FD]" />
              </div>
              <div>
                <p className="font-medium text-white">{t('auth:register.b3', 'Datenschutzgarantie')}</p>
                <p className="text-sm text-slate-500">{t('auth:register.b3d', 'DSGVO-konform und sicher')}</p>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="relative z-10 text-sm text-slate-500">
          © {new Date().getFullYear()} Vyndesk. Alle Rechte vorbehalten.
        </div>
      </div>

      {/* Right Side - Register Form */}
      <div className="flex-1 flex flex-col justify-center items-center px-8 sm:px-12 lg:px-16 bg-white overflow-y-auto">
        <div className="w-full max-w-[440px] space-y-8 py-8">
          {/* Mobile Logo */}
          <div className="lg:hidden text-center mb-8">
            <img 
              src="/logo-full.png" 
              alt="VynDesk Logo" 
              className="h-8 w-auto mx-auto"
            />
          </div>

          <div className="text-center space-y-2">
            <h2 className="text-2xl font-bold text-slate-900">
              {t('auth:register.submit', 'Konto erstellen')}
            </h2>
            <p className="text-slate-500 text-sm">
              {t('common:x2.fillFormToStart', 'Füllen Sie das Formular aus, um loszulegen')}
            </p>
          </div>

          {error && (
            <Alert variant="destructive" className="border-red-100 bg-red-50 text-red-900">
              <AlertCircle className="h-4 w-4 text-red-600" />
              <AlertDescription className="text-red-700">{error}</AlertDescription>
            </Alert>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="firstName" className="text-sm font-medium text-slate-700">{t('common:field.firstName', 'Vorname')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                  <Input
                    id="firstName"
                    name="firstName"
                    placeholder="Max"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    className="pl-10 h-11 bg-white border-slate-200 focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="lastName" className="text-sm font-medium text-slate-700">{t('common:field.lastName', 'Nachname')}</Label>
                <div className="relative">
                  <User className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                  <Input
                    id="lastName"
                    name="lastName"
                    placeholder="Mustermann"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    className="pl-10 h-11 bg-white border-slate-200 focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                    required
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">{t('auth:register.email', 'E-Mail-Adresse')}</Label>
              <div className="relative">
                <Mail className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder={t('auth:login.emailPh', 'name@company.com')}
                  value={formData.email}
                  onChange={handleInputChange}
                  className="pl-10 h-11 bg-white border-slate-200 focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">{t('common:field.password', 'Passwort')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                  <Input
                    id="password"
                    name="password"
                    type={showPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.password}
                    onChange={handleInputChange}
                    className="pl-10 pr-10 h-11 bg-white border-slate-200 focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              <div className="space-y-2">
                <Label htmlFor="confirmPassword" className="text-sm font-medium text-slate-700">{t('common:action.confirm', 'Bestätigen')}</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-2.5 h-5 w-5 text-slate-400" />
                  <Input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? "text" : "password"}
                    placeholder="••••••••"
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="pl-10 pr-10 h-11 bg-white border-slate-200 focus:border-[#0F99FD] focus:ring-[#0F99FD]/20 transition-all"
                    required
                    disabled={isLoading}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-3 text-slate-400 hover:text-slate-600 transition-colors"
                  >
                    {showConfirmPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <div className="flex items-start space-x-2">
                <Checkbox
                  id="terms"
                  checked={acceptTerms}
                  onCheckedChange={(checked) => setAcceptTerms(checked as boolean)}
                  className="mt-1 data-[state=checked]:bg-[#0F99FD] data-[state=checked]:border-[#0F99FD]"
                  disabled={isLoading}
                />
                <Label htmlFor="terms" className="text-sm font-normal text-slate-600 leading-snug cursor-pointer">
                  {t('common:x2.iAccept', 'Ich akzeptiere die')}{' '}
                  <Link to="/terms" className="text-[#0F99FD] hover:underline">AGB</Link>
                  {' '}und die{' '}
                  <Link to="/privacy" className="text-[#0F99FD] hover:underline">{t('auth:register.privacy', 'Datenschutzrichtlinie')}</Link>.
                </Label>
              </div>

              <div className="flex items-start space-x-2">
                <Checkbox
                  id="marketing"
                  checked={acceptMarketing}
                  onCheckedChange={(checked) => setAcceptMarketing(checked as boolean)}
                  className="mt-1 data-[state=checked]:bg-[#0F99FD] data-[state=checked]:border-[#0F99FD]"
                  disabled={isLoading}
                />
                <Label htmlFor="marketing" className="text-sm font-normal text-slate-600 leading-snug cursor-pointer">
                  {t('common:x2.wantMarketing', 'Ich möchte Updates und Marketingmitteilungen per E-Mail erhalten.')}
                </Label>
              </div>
            </div>

            <Button
              type="submit"
              className="w-full h-11 bg-[#0F99FD] hover:bg-[#0c84e0] text-white font-semibold transition-all shadow-sm hover:shadow-md mt-2"
              disabled={isLoading || !acceptTerms}
            >
              {isLoading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t('common:x2.creatingAccount', 'Konto wird erstellt...')}
                </span>
              ) : (
                <span className="flex items-center gap-2">
                  {t('auth:register.submit', 'Konto erstellen')} <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="text-center">
            <p className="text-sm text-slate-500">
              {t('auth:register.hasAccount', 'Bereits ein Konto?')}{' '}
              <Link
                to="/login"
                className="font-semibold text-[#0F99FD] hover:text-[#0c84e0] transition-colors"
              >
                {t('auth:login.title', 'Anmelden')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
